
import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { PlatformLegalModalComponent } from '../platform-legal-modal/platform-legal-modal.component';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-login',
  standalone: true,
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, TranslatePipe, FormsModule, PlatformLegalModalComponent, ConfirmationModalComponent],
})
export class LoginComponent implements OnInit {
  private authService = inject(AuthService);
  private router: Router = inject(Router);
  private notificationService = inject(NotificationService);

  loginForm: FormGroup;

  // Initialization State
  isInitializing = signal(true); 
  
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  
  // UI States
  showPassword = signal(false);
  rememberMe = signal(false);

  // Legal Modal
  isLegalModalOpen = signal(false);
  legalModalTab = signal<'tos' | 'privacy'>('tos');

  // Confirmation Modal State
  confirmModalState = signal({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonText: 'Confirm',
    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
    action: null as (() => Promise<void>) | null
  });

  constructor() {
    const fb: FormBuilder = inject(FormBuilder);
    this.loginForm = fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit(): void {
      // --- STRATEGY: Surgical Wipe (Hard Reset) ---
      this.performSurgicalCleanup();

      // --- Setup UI ---
      const savedEmail = localStorage.getItem('remembered_email');
      if (savedEmail) {
          this.loginForm.patchValue({ email: savedEmail });
          this.rememberMe.set(true);
      } else {
          this.loginForm.reset();
      }

      // Unlock UI immediately
      this.isInitializing.set(false);
  }

  private performSurgicalCleanup() {
      try {
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (!key) continue;
              if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
                  keysToRemove.push(key);
              }
              if (key === 'supabase.auth.token') {
                  keysToRemove.push(key);
              }
              if (key === 'custom_pin_token') {
                  keysToRemove.push(key);
              }
          }
          keysToRemove.forEach(k => localStorage.removeItem(k));
          sessionStorage.clear();
      } catch (e) {
          console.warn('Surgical cleanup warning:', e);
      }
  }

  togglePasswordVisibility(): void {
      this.showPassword.update(v => !v);
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const { email, password } = this.loginForm.value;
    
    if (this.rememberMe()) {
        localStorage.setItem('remembered_email', email);
    } else {
        localStorage.removeItem('remembered_email');
    }

    const result = await this.authService.login(email, password);

    this.isLoading.set(false);

    if (result.success) {
      this.handleSuccessfulLogin();
    } else {
      // UPDATED: ใช้ NotificationService เพื่อจัดการ Error ให้เป็นภาษาไทยและดูเป็นมิตร
      this.notificationService.handleError(result.error || 'An unknown login error occurred.');
    }
  }

  async loginWithGoogle(): Promise<void> {
    this.isLoading.set(true);
    const result = await this.authService.loginWithGoogle();
    if (!result.success) {
        this.notificationService.handleError(result.error || 'Google login failed.');
        this.isLoading.set(false);
    }
  }

  private handleSuccessfulLogin() {
      const user = this.authService.currentUser();
      const store = this.authService.currentStore();
      
      if (!user) {
          this.notificationService.showError("Login succeeded but user state is missing.");
          return;
      }

      if (user.role === 'platform_admin') {
          this.router.navigate(['/platform']);
      } else if (user.role === 'owner' && store && store.setupCompleted === false) {
          this.router.navigate(['/onboarding']);
      } else {
          this.router.navigate(['/admin/customers']);
      }
  }

  openLegalModal(tab: 'tos' | 'privacy') {
    this.legalModalTab.set(tab);
    this.isLegalModalOpen.set(true);
  }

  closeLegalModal() {
    this.isLegalModalOpen.set(false);
  }

  resetDeviceData(): void {
      this.confirmModalState.set({
        isOpen: true,
        title: 'ยืนยันการล้างข้อมูล (Reset Device)',
        message: 'ข้อมูลการตั้งค่าในเครื่องนี้ (Cache, Tokens) จะถูกล้างทั้งหมดเพื่อแก้ปัญหาการเข้าสู่ระบบ ยืนยันหรือไม่?',
        confirmButtonText: 'ล้างข้อมูล (Reset)',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        action: async () => {
            this.isLoading.set(true);
            try {
                localStorage.clear();
                sessionStorage.clear();
                this.notificationService.showSuccess('ล้างข้อมูลสำเร็จ ระบบจะรีโหลดทันที');
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } catch (e) {
                 window.location.reload();
            }
        }
    });
  }

  closeConfirmModal() {
    this.confirmModalState.update(s => ({ ...s, isOpen: false }));
  }

  async onConfirmAction() {
    const action = this.confirmModalState().action;
    this.closeConfirmModal();
    if (action) await action();
  }
}
